import { Box, Button, HStack, Text, VStack } from '@aj/ui';

import { ErrorBoundary } from './ErrorBoundary.js';
import { useT } from '../providers/I18nProvider.js';

/**
 * What a student sees if a screen throws.
 *
 * The copy does one job before it does anything else: say the record is
 * safe. It is — the log lives in IndexedDB and a component that failed to
 * render never touched it. Everything else on this screen is secondary to
 * that sentence.
 *
 * Reload is the primary action, not "try again". A render that threw once
 * from the data it was given will throw again on a re-mount, and offering a
 * button that visibly does nothing is worse than offering none.
 *
 * The technical line is kept, collapsed. It is the only diagnostic anyone
 * will ever have: there is no crash reporter, and once backups are encrypted
 * nobody can inspect a user's data to work out what happened.
 */
export function CrashScreen({ children }: { children: React.ReactNode }) {
  const t = useT();

  return (
    <ErrorBoundary
      fallback={(error, reset) => (
        <VStack
          as="main"
          gap={5}
          align="center"
          justify="center"
          className="min-h-dvh px-6 py-12 text-center"
        >
          <VStack gap={2} align="center">
            <Text variant="title" tone="ink">
              {t('crash.title')}
            </Text>
            <Text variant="body" tone="muted" className="max-w-sm text-pretty">
              {t('crash.safe')}
            </Text>
          </VStack>

          <HStack gap={2} wrap justify="center">
            <Button onClick={() => window.location.reload()}>{t('crash.reload')}</Button>
            <Button variant="secondary" onClick={reset}>
              {t('crash.back')}
            </Button>
          </HStack>

          <Box as="section" className="w-full max-w-sm">
            <details>
              <summary className="cursor-pointer text-sm text-subtle">
                {t('crash.details')}
              </summary>
              <Text
                variant="caption"
                tone="subtle"
                className="mt-2 block overflow-x-auto text-left font-mono whitespace-pre-wrap"
              >
                {error.message}
              </Text>
            </details>
          </Box>
        </VStack>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
